<template>
  <div>
    <fieldset class="natural-survey-fieldset mt-1">
      <legend class="natural-survey-fieldset__legend">الرفع المساحي</legend>
      <div class="natural-survey-fieldset__body survey-method-selector">
        <survey-method-selector
          v-if="projectId"
          :project-id="projectId"
          :current-method-id="surveyUploadMethodId"
          embedded
          @updated="onSurveyMethodSelectorUpdated"
        />

        <div class="coordinates-section">
          <div
            v-if="loadLocation"
            class="text-center py-3"
          >
            <b-spinner
              variant="primary"
              small
              class="mb-1"
            />
            <p class="text-muted mb-0 small">
              جاري تحميل القطع...
            </p>
          </div>
          <b-tabs
            v-else-if="hasLocations"
            v-model="activeLocationTabIndex"
            lazy
          >
            <b-tab
              v-for="(location, index) in projectLocations"
              :key="location.id"
              :title="getLocationTitle(location, index)"
            >
              <coordinate-display-card
                :key="`technical-${location.id || index}`"
                ref="activeCoordinateCard"
                always-show-map
                enable-save-as-new-location
                :coordinate="getCoordinatesByType(getMapFocusedLocation(location).coordinates, 'technical_office')"
                :title="`إحداثيات المكتب الهندسي #`"
                :subtitle="`موقع ${index + 1} - ${getLocationValue(getMapFocusedLocation(location).land_number)}`"
                icon="BuildingIcon"
                icon-variant="bg-danger-light"
                icon-color="danger"
                :show-tabs="true"
                :show-map-button="true"
                :project-id="projectId"
                :project-location-id="getMapFocusedLocation(location).id"
                :project-locations="projectLocations"
                :surveying-code="currentProject && currentProject.surveying_code"
                no-coordinates-message="لا توجد إحداثيات — ارسم المضلع على الخريطة ثم احفظه"
                @view-on-map="viewCoordinateOnMap"
                @coordinates-saved="onLocationCoordinatesSaved"
                @save-as-new-location="onSavePolygonAsNewLocation"
                @create-location="onMapCreateLocation"
                @edit-location-data="onMapEditLocationData"
                @edit-location-polygon="onMapEditLocationPolygon"
              />
            </b-tab>
          </b-tabs>
          <b-alert
            v-else
            show
            variant="info"
            class="mb-0"
          >
            لا توجد قطع لهذا المشروع. استخدم الخريطة لإضافة قطعة جديدة.
          </b-alert>
        </div>
      </div>
    </fieldset>

    <b-modal
      id="method-survey-add-location-modal"
      v-model="showAddLocationModal"
      :title="editLocationId ? 'تعديل القطعة' : (pendingNewLocationPolygonCoordinates.length >= 3 ? 'إضافة قطعة جديدة من المضلع' : 'إضافة موقع جديد')"
      hide-footer
      centered
      size="sm"
      no-enforce-focus
      no-fade
      @show="onAddLocationModalShow"
      @shown="onAddLocationModalShown"
      @hidden="onAddLocationModalHidden"
    >
      <b-form @submit.prevent="handleAddLocationFormSubmit">
        <b-alert
          v-if="pendingNewLocationPolygonCoordinates.length >= 3 && !editLocationId"
          show
          variant="info"
          class="mb-1"
        >
          سيتم حفظ المضلع المرسوم ({{ pendingNewLocationPolygonCoordinates.length }} نقطة) مع القطعة الجديدة.
        </b-alert>
        <b-row>
          <b-col
            sm="12"
            md="6"
          >
            <b-form-group
              label="رقم القطعة *"
              label-for="method-survey-land-number-input"
            >
              <b-form-input
                id="method-survey-land-number-input"
                v-model.trim="addLocationForm.land_number"
                placeholder="ادخل رقم القطعة"
                :disabled="addingLocation"
                required
              />
            </b-form-group>
          </b-col>
          <b-col
            sm="12"
            md="6"
          >
            <b-form-group
              label="رقم البلوك"
              label-for="method-survey-block-number-input"
            >
              <b-form-input
                id="method-survey-block-number-input"
                v-model.trim="addLocationForm.block_number"
                placeholder="ادخل رقم البلوك (اختياري)"
                :disabled="addingLocation"
              />
            </b-form-group>
          </b-col>
          <b-col
            sm="12"
            md="6"
          >
            <b-form-group
              label="المساحة"
              label-for="method-survey-location-area-input"
            >
              <b-form-input
                id="method-survey-location-area-input"
                v-model.number="addLocationForm.area"
                type="number"
                min="0"
                step="0.01"
                placeholder="ادخل المساحة (اختياري)"
                :disabled="addingLocation"
              />
            </b-form-group>
          </b-col>
          <b-col
            sm="12"
            md="6"
          >
            <b-form-group
              label="النشاط"
              label-for="method-survey-location-activity"
            >
              <b-form-radio-group
                id="method-survey-location-activity"
                v-model="addLocationForm.activity"
                :options="activityOptions"
                name="method_survey_activity"
                stacked
                :disabled="addingLocation"
              />
            </b-form-group>
          </b-col>
          <b-col
            sm="12"
            md="6"
          >
            <b-form-group
              label="الوضع"
              label-for="method-survey-location-situation"
            >
              <b-form-radio-group
                id="method-survey-location-situation"
                v-model="addLocationForm.situation"
                :options="situationOptions"
                name="method_survey_situation"
                stacked
                :disabled="addingLocation"
              />
            </b-form-group>
          </b-col>
          <b-col
            sm="12"
            md="6"
          >
            <b-form-group
              label="الترميز"
              label-for="method-survey-location-code"
            >
              <b-form-input
                id="method-survey-location-code"
                v-model.trim="addLocationForm.code"
                :disabled="addingLocation"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              label="ملاحظات القطعة"
              label-for="method-survey-location-notes-input"
            >
              <b-form-textarea
                id="method-survey-location-notes-input"
                v-model.trim="addLocationForm.location_notes"
                rows="3"
                max-rows="5"
                placeholder="ادخل ملاحظات (اختياري)"
                :disabled="addingLocation"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            size="sm"
            :disabled="addingLocation"
            @click="showAddLocationModal = false"
          >
            إلغاء
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            size="sm"
            :disabled="addingLocation || !addLocationForm.land_number"
          >
            <b-spinner
              v-if="addingLocation"
              small
              class="mr-50"
            />
            {{ editLocationId ? 'حفظ التعديلات' : (pendingNewLocationPolygonCoordinates.length >= 3 ? 'حفظ القطعة والمضلع' : 'حفظ القطعة') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import {
  BFormRadioGroup,
  BSpinner,
  BTabs,
  BTab,
  BAlert,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BRow,
  BCol,
  BButton,
} from 'bootstrap-vue'
import CoordinateDisplayCard from '@/components/CoordinateDisplayCard.vue'
import SurveyMethodSelector from '@/views/modules/projects/SurveyMethodSelector.vue'
import SurveyMethods from '@/api/survey-methods/surveyMethods'

const surveyMethodsApi = new SurveyMethods()

export default {
  name: 'MethodSurveyComponent',
  components: {
    BFormRadioGroup,
    BSpinner,
    BTabs,
    BTab,
    BAlert,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
    BCol,
    BButton,
    CoordinateDisplayCard,
    SurveyMethodSelector,
  },
  props: {
    projectId: {
      type: Number,
      required: true,
    },
    project: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters({
      surveyMethod: 'projects/surveyMethod',
      storeProject: 'projects/project',
      locationsData: 'projects/locationsData',
      loadLocation: 'projects/loadLocation',
    }),
    currentProject() {
      return this.project || this.storeProject || {}
    },
    projectLocations() {
      return this.locationsData?.project_locations || []
    },
    hasLocations() {
      return this.projectLocations.length > 0
    },
  },
  data() {
    return {
      loadingSurveyMethods: false,
      savingSurveyMethod: false,
      surveyUploadMethodId: null,
      surveyUploadMethods: [],
      selectedFixedPointId: null,
      selectedStaticPointId: null,
      selectedCorrectedStaticPointId: null,
      isProcessed: false,
      surveyComments: '',
      activeLocationTabIndex: 0,
      previousLocationTabIndex: 0,
      pendingLocationTabFocusIndex: null,
      mapFocusLocationId: null,
      pendingPolygonEditLocationId: null,
      showAddLocationModal: false,
      keepMapFullscreenForModal: false,
      editLocationId: null,
      addingLocation: false,
      pendingNewLocationPolygonCoordinates: [],
      addLocationForm: {
        land_number: '',
        block_number: '',
        area: null,
        activity: null,
        situation: null,
        code: '',
        location_notes: '',
        project_id: null,
      },
      activityOptions: [
        { text: 'مبني', value: 'مبني' },
        { text: 'أرض', value: 'أرض' },
      ],
      situationOptions: [
        { text: 'مطلوب التقنيين', value: 'مطلوب التقنيين' },
        { text: 'اشغالات', value: 'اشغالات' },
        { text: 'ملكية خاصة', value: 'ملكية خاصة' },
      ],
    }
  },
  methods: {
    async fetchSurveyMethods() {
      this.loadingSurveyMethods = true
      try {
        const response = await surveyMethodsApi.list()
        if (response.data && Array.isArray(response.data)) {
          this.surveyUploadMethods = response.data
            .sort((a, b) => {
              const orderA = a.order || 0
              const orderB = b.order || 0
              return orderA - orderB
            })
            .map(method => ({
              value: Number(method.id),
              text: method.name,
              id: method.id,
            }))
        } else {
          this.surveyUploadMethods = []
        }
      } catch (error) {
        console.error('Failed to fetch survey methods:', error)
        this.surveyUploadMethods = [
          { value: 1, text: 'الرفع كاملاً VRS لجميع نقاط الرفع' },
          { value: 2, text: 'الرفع نقطة ثابتة VRS وتكملة RTK' },
          { value: 3, text: 'الرفع STATIC لأول مرة لذات الثابتة الغير معالجة مع RTK' },
          { value: 4, text: 'الرفع RTK بمعلومية نقطة مصححة ومعتمدة باساس STATIC' },
        ]
      } finally {
        this.loadingSurveyMethods = false
      }
    },
    async loadSurveyMethod() {
      try {
        const response = await this.$store.dispatch('projects/getSurveyMethod', {
          id: this.projectId,
        })

        if (response && response.data && response.success) {
          const { data } = response
          this.surveyUploadMethodId = data.survey_method?.id
          this.selectedFixedPointId = data.selected_fixed_point_id
          this.selectedStaticPointId = data.selected_static_point_id
          this.selectedCorrectedStaticPointId = data.selected_corrected_static_point_id
          this.isProcessed = data.is_processed || false
          this.surveyComments = data.survey_comments || ''

          if (typeof this.loadMethodSpecificData === 'function') {
            await this.loadMethodSpecificData()
          }
          if (this.selectedFixedPointId && typeof this.loadSelectedFixedPointRawDataMedia === 'function') {
            await this.loadSelectedFixedPointRawDataMedia()
          }
          if (this.selectedStaticPointId && typeof this.loadSelectedStaticPointMedia === 'function') {
            await this.loadSelectedStaticPointMedia()
          }
        }
      } catch (error) {
        if (error.response && error.response.status !== 404) {
          console.error('Error loading survey method:', error)
        }
      }
    },
    async saveSurveyMethod() {
      if (!this.surveyUploadMethodId) return
      this.savingSurveyMethod = true
      try {
        const payload = {
          survey_method_id: this.surveyUploadMethodId,
          selected_fixed_point_id: this.selectedFixedPointId,
          selected_static_point_id: this.selectedStaticPointId,
          selected_corrected_static_point_id: this.selectedCorrectedStaticPointId,
          is_processed: this.isProcessed,
          survey_comments: this.surveyComments,
        }

        let response
        try {
          response = await this.$store.dispatch('projects/updateSurveyMethod', {
            id: this.projectId,
            query: payload,
          })
          this.$toast.success('تم حفظ طريقة الرفع المساحي بنجاح')
        } catch (error) {
          this.$toast.error(error.response?.data?.message || 'تعذر حفظ طريقة الرفع المساحي')
        }

        if (response && response.data && response.data.success) {
          this.$toast.success('تم حفظ طريقة الرفع المساحي بنجاح')
          if (typeof this.initializeMethodSpecificData === 'function') {
            await this.initializeMethodSpecificData()
          }
        }
      } catch (error) {
        console.error('Error saving survey method:', error)
        const errorMsg = error.response?.data?.message || 'تعذر حفظ طريقة الرفع المساحي'
        this.$toast.error(errorMsg)
      } finally {
        this.savingSurveyMethod = false
      }
    },
    onSurveyMethodSelectorUpdated(payload = {}) {
      if (payload.survey_method_id != null) {
        this.surveyUploadMethodId = Number(payload.survey_method_id)
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'selected_fixed_point_id')) {
        this.selectedFixedPointId = payload.selected_fixed_point_id
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'selected_static_point_id')) {
        this.selectedStaticPointId = payload.selected_static_point_id
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'selected_corrected_static_point_id')) {
        this.selectedCorrectedStaticPointId = payload.selected_corrected_static_point_id
      }
    },
    onSurveyMethodChange() {
      this.saveSurveyMethod()
    },
    initLocations() {
      this.$store.dispatch('projects/getSpatialData', { id: this.projectId })
        .catch(error => {
          console.error('Error fetching project locations:', error)
          this.$bvToast.toast('تعذر تحميل بيانات القطع', {
            title: 'خطأ',
            variant: 'danger',
            solid: true,
          })
        })
    },
    getLocationTitle(location, index) {
      if (location.land_number) {
        return `قطعة رقم: ${location.land_number}`
      }
      return `Location ${index + 1}`
    },
    getLocationValue(value) {
      return value || 'N/A'
    },
    getCoordinatesByType(coordinates, type) {
      if (!coordinates) return []
      return coordinates.filter(coord => coord.type === type)
    },
    getMapFocusedLocation(tabLocation) {
      const locations = this.projectLocations
      if (this.mapFocusLocationId != null && this.mapFocusLocationId !== '') {
        const focused = locations.find(item => Number(item.id) === Number(this.mapFocusLocationId))
        if (focused) return focused
      }
      return tabLocation
    },
    viewCoordinateOnMap() {},
    onLocationCoordinatesSaved(payload) {
      if (payload?.locationId && Array.isArray(payload?.coordinates)) {
        this.patchLocationCoordinates(
          payload.locationId,
          payload.coordinates,
          payload.coordinates_type || 'technical_office',
        )
        return
      }
      this.initLocations()
    },
    upsertProjectLocation(location) {
      if (!location || location.id == null) return
      this.$store.commit('projects/UPSERT_PROJECT_LOCATION', location)
    },
    patchLocationCoordinates(locationId, coordinates, coordinatesType = 'technical_office') {
      if (!locationId || !Array.isArray(coordinates)) return
      this.$store.commit('projects/PATCH_PROJECT_LOCATION_COORDINATES', {
        locationId,
        coordinates,
        coordinatesType,
      })
    },
    getActiveCoordinateDisplayCard() {
      const ref = this.$refs.activeCoordinateCard
      if (Array.isArray(ref)) {
        return ref.find(Boolean) || null
      }
      return ref || null
    },
    getDocumentFullscreenElement() {
      const doc = document
      return doc.fullscreenElement
        || doc.webkitFullscreenElement
        || doc.mozFullScreenElement
        || doc.msFullscreenElement
        || null
    },
    async restoreActiveMapFullscreen() {
      await this.$nextTick()
      const card = this.getActiveCoordinateDisplayCard()
      if (!card?.enterActiveMapFullscreen) return false
      return card.enterActiveMapFullscreen()
    },
    bringModalAboveFullscreenMap(modalId = 'method-survey-add-location-modal') {
      const fsEl = this.getDocumentFullscreenElement()
      if (!fsEl) return false
      const modal = document.getElementById(modalId)
      document.querySelectorAll('.modal-backdrop').forEach(backdrop => {
        if (!fsEl.contains(backdrop)) {
          fsEl.appendChild(backdrop)
        }
      })
      if (modal && modal.parentElement !== fsEl) {
        fsEl.appendChild(modal)
      }
      return !!(modal && fsEl.contains(modal))
    },
    async keepMapFullscreenAroundModal() {
      this.bringModalAboveFullscreenMap()
      await this.$nextTick()
      this.bringModalAboveFullscreenMap()
      if (this.keepMapFullscreenForModal && !this.getDocumentFullscreenElement()) {
        await this.restoreActiveMapFullscreen()
        await this.$nextTick()
        this.bringModalAboveFullscreenMap()
      }
    },
    markKeepMapFullscreenForModal() {
      this.keepMapFullscreenForModal = !!this.getDocumentFullscreenElement()
    },
    onAddLocationModalShow() {
      this.keepMapFullscreenAroundModal()
    },
    onAddLocationModalShown() {
      this.keepMapFullscreenAroundModal()
    },
    resetAddLocationForm() {
      this.addLocationForm = {
        land_number: '',
        block_number: '',
        area: null,
        activity: null,
        situation: null,
        code: '',
        location_notes: '',
        project_id: this.projectId,
      }
      this.editLocationId = null
      this.addingLocation = false
    },
    onAddLocationModalHidden() {
      const restoreFullscreen = this.keepMapFullscreenForModal && !this.getDocumentFullscreenElement()
      this.resetAddLocationForm()
      this.pendingNewLocationPolygonCoordinates = []
      this.keepMapFullscreenForModal = false
      if (restoreFullscreen) {
        this.restoreActiveMapFullscreen()
      }
    },
    handleAddLocationFormSubmit() {
      if (this.pendingNewLocationPolygonCoordinates.length >= 3 && !this.editLocationId) {
        return this.submitNewLocationWithPolygon()
      }
      return this.submitLocationForm()
    },
    async onSavePolygonAsNewLocation(payload) {
      const coordinates = Array.isArray(payload?.coordinates) ? payload.coordinates : []
      if (coordinates.length < 3) {
        this.$swal({
          icon: 'warning',
          title: 'لا يوجد مضلع مرسوم للحفظ',
          text: 'ارسم مضلعاً على الخريطة (3 نقاط على الأقل) ثم اضغط زر حفظ كقطعة جديدة.',
          showConfirmButton: true,
          confirmButtonColor: '#06284D',
          allowOutsideClick: true,
        })
        return
      }

      this.pendingNewLocationPolygonCoordinates = coordinates.map(coord => ({
        ...coord,
        type: coord?.type || payload?.coordinates_type || 'technical_office',
      }))
      this.resetAddLocationForm()
      this.markKeepMapFullscreenForModal()
      this.showAddLocationModal = true
      this.keepMapFullscreenAroundModal()
    },
    AddNewLocation() {
      this.resetAddLocationForm()
      this.showAddLocationModal = true
    },
    onMapCreateLocation() {
      this.markKeepMapFullscreenForModal()
      this.AddNewLocation()
      this.keepMapFullscreenAroundModal()
    },
    editLocation(location) {
      this.editLocationId = location.id
      this.addLocationForm = {
        land_number: location.land_number || '',
        block_number: location.block_number || '',
        area: location.area ?? null,
        activity: location.activity ?? null,
        situation: location.situation ?? null,
        code: location.code != null ? String(location.code) : '',
        location_notes: location.location_notes || '',
        project_id: this.projectId,
      }
      this.showAddLocationModal = true
    },
    onMapEditLocationData(location) {
      this.markKeepMapFullscreenForModal()
      if (!location) {
        this.AddNewLocation()
        return
      }
      this.editLocation(location)
      this.keepMapFullscreenAroundModal()
    },
    onMapEditLocationPolygon(location) {
      if (!location?.id) return
      const inFullscreen = !!this.getDocumentFullscreenElement()
      this.mapFocusLocationId = location.id
      this.pendingPolygonEditLocationId = location.id
      if (!inFullscreen) {
        this.focusLocationTab(location.id)
      }
      this.$nextTick(() => this.flushPendingPolygonEdit())
    },
    async flushPendingPolygonEdit(attempt = 0) {
      const locationId = this.pendingPolygonEditLocationId
      if (!locationId) return
      const card = this.getActiveCoordinateDisplayCard()
      const cardLocationId = card?.projectLocationId
      if (!card || Number(cardLocationId) !== Number(locationId)) {
        if (attempt < 25) {
          window.setTimeout(() => this.flushPendingPolygonEdit(attempt + 1), 80)
        }
        return
      }
      this.pendingPolygonEditLocationId = null
      const location = this.projectLocations.find(item => Number(item.id) === Number(locationId))
      await card.beginParcelPolygonEdit(location || null)
    },
    focusLocationTab(locationId) {
      const tabIndex = this.projectLocations.findIndex(loc => Number(loc.id) === Number(locationId))
      if (tabIndex >= 0) {
        this.focusLocationTabByIndex(tabIndex)
      }
    },
    focusLocationTabByIndex(tabIndex) {
      if (tabIndex < 0) return
      this.pendingLocationTabFocusIndex = tabIndex
      const applyFocus = () => {
        this.activeLocationTabIndex = tabIndex
        this.previousLocationTabIndex = tabIndex
      }
      applyFocus()
      this.$nextTick(() => {
        applyFocus()
        window.setTimeout(() => {
          applyFocus()
          this.pendingLocationTabFocusIndex = null
        }, 50)
      })
    },
    bringSweetAlertAboveFullscreen() {
      const fsEl = this.getDocumentFullscreenElement()
      if (!fsEl) return
      document.querySelectorAll('.swal2-container').forEach(el => {
        if (!fsEl.contains(el)) {
          fsEl.appendChild(el)
        }
      })
    },
    async showSwalAboveFullscreen(options) {
      const resultPromise = this.$swal({
        returnFocus: false,
        ...options,
        didOpen: () => {
          this.bringSweetAlertAboveFullscreen()
          if (typeof options.didOpen === 'function') options.didOpen()
        },
      })
      await this.$nextTick()
      this.bringSweetAlertAboveFullscreen()
      requestAnimationFrame(() => this.bringSweetAlertAboveFullscreen())
      return resultPromise
    },
    async submitNewLocationWithPolygon() {
      if (!this.addLocationForm.land_number || this.addingLocation) return
      if (!Array.isArray(this.pendingNewLocationPolygonCoordinates) || this.pendingNewLocationPolygonCoordinates.length < 3) {
        this.$swal({
          icon: 'warning',
          title: 'لا يوجد مضلع مرسوم للحفظ',
          showConfirmButton: true,
          confirmButtonColor: '#06284D',
          allowOutsideClick: true,
        })
        return
      }

      const keepFullscreen = !!this.getDocumentFullscreenElement() || this.keepMapFullscreenForModal
      this.addingLocation = true
      try {
        const payload = {
          query: {
            ...this.addLocationForm,
            project_id: this.projectId,
          },
        }

        const response = await this.$store.dispatch('projects/addLocation', payload)
        const savedLocation = response?.data || null
        const newLocationId = savedLocation?.id

        if (newLocationId) {
          const coordinates = this.pendingNewLocationPolygonCoordinates.map(coord => ({
            ...coord,
            type: coord.type || 'technical_office',
          }))

          await this.$store.dispatch('projects/updateCoordinates', {
            id: newLocationId,
            query: {
              coordinates,
              coordinates_type: 'technical_office',
            },
          })

          this.upsertProjectLocation({
            ...savedLocation,
            coordinates,
          })

          const tabIndex = this.projectLocations.findIndex(loc => Number(loc.id) === Number(newLocationId))
          this.showAddLocationModal = false
          this.resetAddLocationForm()
          if (keepFullscreen) {
            this.mapFocusLocationId = newLocationId
          } else {
            this.focusLocationTabByIndex(tabIndex >= 0 ? tabIndex : this.projectLocations.length - 1)
          }
        } else {
          this.showAddLocationModal = false
          this.resetAddLocationForm()
          if (!keepFullscreen) {
            this.initLocations()
          }
        }

        if (keepFullscreen && !this.getDocumentFullscreenElement()) {
          await this.restoreActiveMapFullscreen()
        }

        await this.showSwalAboveFullscreen({
          icon: 'success',
          title: `<h4>${response.message || 'تم إضافة القطعة وحفظ المضلع بنجاح'}</h4>`,
          showConfirmButton: true,
          confirmButtonColor: '#06284D',
          allowOutsideClick: true,
        })
      } catch (error) {
        if (keepFullscreen && !this.getDocumentFullscreenElement()) {
          await this.restoreActiveMapFullscreen()
        }
        await this.showSwalAboveFullscreen({
          icon: 'error',
          title: error.response?.data?.message || 'تعذر إضافة القطعة وحفظ المضلع',
          showConfirmButton: true,
          confirmButtonColor: '#06284D',
          allowOutsideClick: true,
        })
      } finally {
        this.addingLocation = false
      }
    },
    async submitLocationForm() {
      if (!this.addLocationForm.land_number || this.addingLocation) return

      const keepFullscreen = !!this.getDocumentFullscreenElement() || this.keepMapFullscreenForModal
      this.addingLocation = true
      try {
        let payload = {
          query: {
            ...this.addLocationForm,
            project_id: this.projectId,
          },
        }
        if (this.editLocationId) {
          payload = { ...payload, id: this.editLocationId }
        }

        const response = await this.$store.dispatch('projects/addLocation', payload)
        const savedLocation = response?.data || null
        const wasEdit = !!this.editLocationId

        if (savedLocation) {
          this.upsertProjectLocation(savedLocation)
          const tabIndex = wasEdit
            ? this.projectLocations.findIndex(loc => Number(loc.id) === Number(savedLocation.id))
            : this.projectLocations.length - 1

          this.showAddLocationModal = false
          this.resetAddLocationForm()
          if (keepFullscreen) {
            if (savedLocation.id != null) this.mapFocusLocationId = savedLocation.id
            if (!this.getDocumentFullscreenElement()) {
              await this.restoreActiveMapFullscreen()
            }
          } else {
            this.focusLocationTabByIndex(tabIndex >= 0 ? tabIndex : this.projectLocations.length - 1)
          }
        } else {
          this.showAddLocationModal = false
          this.resetAddLocationForm()
          if (keepFullscreen && !this.getDocumentFullscreenElement()) {
            await this.restoreActiveMapFullscreen()
          }
        }
        await this.showSwalAboveFullscreen({
          icon: 'success',
          title: `<h4>${response.message || (wasEdit ? 'تم تعديل القطعة بنجاح' : 'تم إضافة القطعة بنجاح')}</h4>`,
          showConfirmButton: true,
          confirmButtonColor: '#06284D',
          allowOutsideClick: true,
        })
      } catch (error) {
        if (keepFullscreen && !this.getDocumentFullscreenElement()) {
          await this.restoreActiveMapFullscreen()
        }
        await this.showSwalAboveFullscreen({
          icon: 'error',
          title: error.response?.data?.message || (this.editLocationId ? 'تعذر تعديل القطعة' : 'تعذر إضافة القطعة'),
          showConfirmButton: true,
          confirmButtonColor: '#06284D',
          allowOutsideClick: true,
        })
      } finally {
        this.addingLocation = false
      }
    },
  },
  mounted() {
    this.fetchSurveyMethods()
    this.loadSurveyMethod()
    this.initLocations()
  },
}
</script>
<style scoped>
.natural-survey-fieldset {
  min-width: 0;
  margin: 0.65rem 0 0.85rem;
  padding: 0.85rem 1rem 1rem;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  direction: ltr;
}

.natural-survey-fieldset > .natural-survey-fieldset__legend {
  float: none !important;
  display: table;
  width: auto !important;
  max-width: calc(100% - 1.5rem);
  margin: 0 0 0 0.75rem !important;
  padding: 0 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.2;
  color: #5e5873;
  text-align: left;
  background-color: #fff;
}


</style>
